<template>
  <portal to="MODAL">
    <vk-modal class="MODAL" :class="backdropClass" @close="closeModal">
      <form
          @submit.prevent="checkForRange">
        <vk-modal-body class="MODAL-WIDTH">
          <vk-modal-header class="text-bold c-depot">
            <div class="p-l-6 text-bold text-center c-gray90">Pause My Leads</div>
          </vk-modal-header>
          <vk-modal-content class=" d-flex fd-column ai-center">
            <div class="MODAL-CONTENT p-x-2:mo">
              <div class=" text-bold text-lg p-b-2">
                <span>Select Occupations</span>
              </div>
              <div class="p-b-2 LISTBOX">
                <vk-listbox
                    v-model="selected"
                    :display-text="displayText"
                    :disabled="isEditing"
                >
                  <vk-listbox-option
                      v-for="(value, index) in occupationsList"
                      :key="index"
                      :value="value">
                    {{ value }}
                  </vk-listbox-option>
                </vk-listbox>
              </div>
              <div class="p-b-2">
                <div class="d-flex fd-row jc-between">
                  <div class="fd-column ">
                    <div class="text-bold LABEL ">Start Date</div>
                    <input
                        class="INPUT"
                        placeholder='mm/dd/yyyy'
                        v-model="displayRange.start"
                        @keyup="event => checkForValidDate(event.target.value, true)"
                    >
                  </div>
                  <div class="fd-column ">
                    <div class="text-bold LABEL">End Date</div>
                    <input
                        class="INPUT"
                        placeholder='mm/dd/yyyy'
                        v-model="displayRange.end"
                        @keyup="event => checkForValidDate(event.target.value, false)"
                    >
                  </div>
                </div>
              </div>
              <div class="d-flex jc-center custom-calendar w-100 p-b-1">
                <v-calendar
                    :attributes="attribs"
                    ref="modalCalendar"
                    :model-config="modelConfig"
                    :value="range"
                    color="depot"
                    @dayclick="modalDayClick"
                    class="w-100"
                    :min-date="today"
                    :max-date="maxDate"

                />
              </div>
              <div v-if="isRangeOverlapping" class="c-red d-flex">
                <vk-icon name="exclamation" class="ERROR-ICON"/>
                <span>Oops! You can’t select a date range with existing pauses.
                Please make new selections to continue.</span>
              </div>
            </div>
            <div class="m-r-6">
              <vk-icon
                  name="information-filled"
                  size="18"
                  class="ICON"
                  unbounded/>
              <span class="TEXT">
                Go to Schedule tab to edit/delete.
              </span>
            </div>
          </vk-modal-content>
          <vk-modal-footer v-if="isEditing" class="MODAL-FOOTER d-flex fd-column ai-center m-b-2">
            <button
                :disabled="isRangeOverlapping"
                type="submit"
                class="btn BUTTON m-b-1"
            >Save this Pause</button>
            <button
                type="button"
                class="btn-ghostfilled BUTTON"
                @click="deletePause"
            ><vk-icon name="trash" class="m-r-1" size="17"/>Delete</button>
          </vk-modal-footer>
          <vk-modal-footer v-else class="MODAL-FOOTER jc-center m-b-2 p-t-0">
            <button
                :disabled="isRangeOverlapping"
                type="submit"
                class="btn BUTTON"
            >Save this Pause</button>
          </vk-modal-footer>
        </vk-modal-body>
      </form>
    </vk-modal>
  </portal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Datepicker from 'vuejs-datepicker';

import {
  format,
  isBefore,
  isAfter,
  isValid,
  isWithinInterval,
  areIntervalsOverlapping,
  subDays,
  addDays,
  startOfDay } from 'date-fns';
import Calendar from 'v-calendar/lib/components/calendar.umd';

export default {
  name: 'availability-modal',
  components: {
    Datepicker,
    'v-calendar': Calendar,
  },
  props: {
    backdropClass: {
      type: String,
    },
    pause: {
      type: Object,
      default () {
        return {};
      },
    },
  },
  data () {
    const today = new Date();
    const maxDate = addDays(new Date(), 30);

    return {
      modelConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD',
      },
      today,
      maxDate,
      selected: '',
      loading: false,
      displayRange: {
        start: null,
        end: null,
      },
      range: {
        start: null,
        end: null,
      },
      disabled: false,
    };
  },
  computed: {
    ...mapGetters('account/pro', [
      'serviceProvidersIds',
      'getOverlappingUnavailabilities',
      'scheduleMessage',
      'serviceProviderCatalogOccupations',
      'unavailabilities',
      'occupationUnavailabilities',
    ]),
    attribs () {
      return [
        // highlight for current pause range
        { highlight: {
          start: {
            style: {
              backgroundColor: '#F96302',
              width: '100%',
            },
            contentStyle: {
              color: '#ffffff',
            },
          },
          base: {
            style: {
              backgroundColor: this.range.start && this.range.end ? 'rgba(249,99,2,0.2)' : 'rgba(0,0,0,0.0)',
            },
          },
          end: {
            style: {
              backgroundColor: '#F96302',
              width: '100%',
            },
            contentStyle: {
              color: '#ffffff',
            },
          },
        },
        dates: this.range,
        },
        // highlight for dates out of allowed range
        { highlight: {
          start: {
            style: {
              backgroundColor: '#ffffff',
              width: '100%',
            },
            contentStyle: {
              color: '#E6E6E6',
            },
          },
          base: {
            style: {
              backgroundColor: '#ffffff',
            },
            contentStyle: {
              color: '#E6E6E6',
            },
          },
          end: {
            style: {
              backgroundColor: '#ffffff',
              width: '100%',
            },
            contentStyle: {
              color: '#E6E6E6',
            },
          },
        },
        dates: [
          {
            start: null,
            end: startOfDay(subDays(new Date(), 1)),
          },
          {
            start: startOfDay(addDays(new Date(), 31)),
            end: null }],
        },
        // highlights for other existing pauses
        { highlight: {
          start: {
            style: {
              backgroundColor: '#C8CBCC',
              width: '100%',
            },
            contentStyle: {
              color: '#43484C',
            },
          },

          base: {
            style: {
              backgroundColor: '#C8CBCC',
            },
            contentStyle: {
              color: '#43484C',
            },
          },
          end: {
            style: {
              backgroundColor: '#C8CBCC',
              width: '100%',
            },
            contentStyle: {
              color: '#43484C',
            },
          },
        },
        dates: this.currentPauses,
        },
        // highlight for current range that starts before current date
        { highlight: {
          start: {
            style: {
              backgroundColor: '#C8CBCC',
              width: '100%',
            },
            contentStyle: {
              color: '#ffffff',
            },
          },

          base: {
            style: {
              backgroundColor: '#C8CBCC',
            },
            contentStyle: {
              color: '#666666',
            },
          },
          end: {
            style: {
              backgroundColor: '#C8CBCC',
              width: '100%',
            },
            contentStyle: {
              color: '#666666',
            },
          },
        },
        dates: this.pastCurrentPauseStart,
        },
      ];
    },
    currentPauses () {
      const currrentPauses = this.selected === this.allOccupations
        ? this.unavailabilities
        : this.unavailabilities.concat(this.occupationUnavailabilities);
      return currrentPauses.filter(singlePause => singlePause.id !== this.pause.id)
        .map((pause) => {
          return {
            start: new Date(`${pause.startDate}T00:00:00`),
            end: new Date(`${pause.endDate}T00:00:00`),
          };
        });
    },
    pastCurrentPauseStart () {
      return isBefore(
        startOfDay(new Date(this.range.start)),
        startOfDay(new Date(this.today))
      )
        ? { start: this.range.start, end: subDays(this.today, 1) }
        : null;
    },
    displayText () {
      return this.selected;
    },
    occupations () {
      return Object.values(
        this.serviceProviderCatalogOccupations
      ).map((oc) => {
        return { [oc.occupation.display_name]: oc.id };
      });
    },
    occupationsList () {
      const occupationList = this.occupations.map(oc => Object.keys(oc)[0]);
      occupationList.unshift(this.allOccupations);
      return occupationList;
    },
    selectedOccupationId () {
      if (this.selected && !this.isAllOccupationsSelected) {
        return this.occupations.filter((oc) => {
          return Object.keys(oc).includes(this.selected);
        })[0][this.selected];
      }
    },
    allOccupations () {
      return `All occupations (${this.occupations.length})`;
    },
    isAllOccupationsSelected () {
      return this.selected === this.occupationsList[0];
    },
    isRangeOverlapping () {
      if (this.range.start && this.range.end && this.currentPauses) {
        const formattedRange = {
          start: new Date(this.range.start),
          end: new Date(this.range.end),
        };

        return this.currentPauses.map((pause) => {
          const formattedPause = {
            start: new Date(pause.start),
            end: new Date(pause.end),
          };
          return (areIntervalsOverlapping((formattedRange), (formattedPause)));
        }).includes(true);
      }
    },
    isEditing () {
      return !!this.pause.id;
    },
    serviceProviderOccupationIds () {
      return Object.keys(this.serviceProviderCatalogOccupations).join(',');
    },
    successMessage () {
      return 'Success! View your paused lead dates in your Schedule.';
    },
  },
  watch: {
    selectedOccupationId (value) {
      if (value) {
        this.fetchSelectedOccupationPauses(value);
      } else {
        this.fetchPauses();
      }
    },
  },
  created () {
    [this.selected] = this.occupationsList;
    this.fetchPauses();
    if (this.isEditing) {
      this.setPauseEditMode();
      this.moveToDate();
    }
  },
  methods: {
    ...mapActions('account/pro', [
      'createServiceProviderUnavailability',
      'fetchServiceProviderUnavailabilities',
      'fetchServiceProviderOccupationUnavailabilities',
      'createServiceProviderOccupationUnavailability',
      'deleteServiceProviderOccupationUnavailability',
      'deleteServiceProviderUnavailability',
      'updateServiceProviderOccupationUnavailability',
      'updateServiceProviderUnavailability',
    ]),

    resetSelectedRange () {
      this.displayRange = {
        start: null,
        end: null,
      };
      this.range = {
        start: null,
        end: null,
      };
    },
    async fetchPauses () {
      try {
        await this.fetchServiceProviderUnavailabilities();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    async fetchSelectedOccupationPauses (id) {
      try {
        const params = {
          from: 0,
          size: 100,
          service_provider_occupation_ids: id,
        };
        await this.fetchServiceProviderOccupationUnavailabilities(params);
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    closeModal () {
      this.$emit('close');
    },
    checkForRange () {
      if (this.range.start && this.range.end) {
        if (this.isEditing) {
          this.updatePause();
        } else {
          this.submit();
        }
      }
    },
    async submit () {
      this.loading = true;
      const params = {
        unavailability_period_type: 'vacation',
        unavailability_period_range: {
          start_date: format(startOfDay(new Date(this.range.start)), 'yyyy-MM-dd'),
          end_date: format(startOfDay(new Date(this.range.end)), 'yyyy-MM-dd'),
        },
        lifecycle_status: 'active',
      };
      try {
        if (this.selectedOccupationId) {
          await this.createServiceProviderOccupationUnavailability({
            serviceProviderOccupationId: this.selectedOccupationId,
            params,
          });
        } else {
          await this.createServiceProviderUnavailability({
            serviceProviderId: this.serviceProvidersIds[0],
            params,
          });
        }
        this.loading = false;
        this.closeModal();
        this.$events.$emit(
          this.$events.PRO_PAUSE_CREATION,
          {
            occupation: this.selectedOccupationId ? this.selected : 'All',
          }
        );
        this.$fireToast({
          message: this.successMessage,
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$events.$emit(this.$events.PRO_PAUSE_CREATION_ERROR, { errorMessage: err });
      }
    },
    modalDayClick (event) {
      const selectedDay = event.date;
      const start = this.range.start ? new Date(this.range.start) : null;
      const end = this.range.end ? new Date(this.range.end) : null;

      const displaySelectedDate = format(new Date(selectedDay), 'MM/dd/yyyy');
      if (this.isRangeOverlapping) {
        this.resetSelectedRange();
      }
      if (isAfter(event.date, addDays(new Date(), 30))
          || isBefore(event.date, subDays(new Date(), 1))) {
        return;
      }
      if (this.isDatePaused(selectedDay)) {
        return;
      }
      if (!this.range.start) {
        this.range.start = selectedDay;
        this.displayRange.start = displaySelectedDate;
      } else if (!this.range.end) {
        this.range.end = selectedDay;
        this.displayRange.end = displaySelectedDate;
        if (isBefore(selectedDay, start)) {
          this.range.end = this.range.start;
          this.displayRange.end = format(new Date(this.range.start), 'MM/dd/yyyy');
          this.range.start = selectedDay;
          this.displayRange.start = displaySelectedDate;
        }
      } else if (isBefore(selectedDay, end) && isAfter(selectedDay, start)) {
        this.range.end = selectedDay;
        this.displayRange.end = displaySelectedDate;
      } else if (isBefore(selectedDay, end)) {
        this.range.start = selectedDay;
        this.displayRange.start = displaySelectedDate;
      } else if (isAfter(selectedDay, end)) {
        this.range.start = selectedDay;
        this.displayRange.start = displaySelectedDate;
        this.range.end = null;
        this.displayRange.end = null;
      }
    },

    isDatePaused (selectedDate) {
      const formattedDay = format(new Date(selectedDate), 'MM/dd/yyyy');
      const currentIntervals = this.currentPauses ? this.currentPauses.map((pause) => {
        const formattedPause = { start: format(new Date(pause.start), 'MM/dd/yyyy'), end: format(new Date(pause.end), 'MM/dd/yyyy') };
        if (isWithinInterval(new Date(formattedDay), {
          start: new Date(formattedPause.start),
          end: new Date(formattedPause.end),
        })) {
          return false;
        }
        return true;
      }) : [];
      return currentIntervals.includes(false);
    },

    checkForValidDate (text, isStartDate) {
      const calendar = this.$refs.modalCalendar;
      if (text.length === 0) {
        if (isStartDate) {
          this.range.start = null;
        } else {
          this.range.end = null;
        }
      }
      if (text.length === 10) {
        if ((!isValid(new Date(text)) || (!isWithinInterval(new Date(text), { start: new Date(),
          end: addDays(new Date(), 30) })))) {
          if (isStartDate) {
            this.range.start = null;
            this.displayRange.start = null;
          } else {
            this.range.end = null;
            this.displayRange.end = null;
          }
        } else if (isStartDate) {
          this.range.start = text;
          calendar.move(text);
        } else if (isBefore(text, this.range.start)) {
          this.range.end = null;
          this.displayRange.end = null;
        } else {
          this.range.end = text;
          calendar.move(text);
        }
      }
    },
    async setPauseEditMode () {
      this.range.start = new Date(`${this.pause.startDate}T00:00:00`);
      this.displayRange.start = format(new Date(this.range.start), 'MM/dd/yyyy');
      this.range.end = new Date(`${this.pause.endDate}T00:00:00`);
      this.displayRange.end = format(new Date(this.range.end), 'MM/dd/yyyy');
      this.selected = this.pause.serviceProviderOccupationId
        ? this.serviceProviderCatalogOccupations[this.pause.serviceProviderOccupationId]
          .occupation.display_name
        : this.allOccupations;
    },
    async deletePause () {
      try {
        if (this.pause.serviceProviderOccupationId) {
          await this.deleteServiceProviderOccupationUnavailability({
            unavailabilityId: this.pause.id,
          });
        } else {
          await this.deleteServiceProviderUnavailability({ unavailabilityId: this.pause.id });
        }
        this.closeModal();
        this.$events.$emit(this.$events.PRO_PAUSE_DELETE,
          {
            occupation: this.selectedOccupationId
              ? this.displayText : 'All',
          }
        );
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        this.$events.$emit(this.$events.PRO_PAUSE_DELETE_ERROR, {
          errorMessage: err,
          occupation: this.deleteItem.serviceProviderOccupationId
            ? this.serviceProviderCatalogOccupations[this.deleteItem.serviceProviderOccupationId]
              .occupation.display_name
            : 'All',
        });
      }
    },
    async updatePause () {
      if (format(new Date(this.range.start), 'yyyy-MM-dd') === this.pause.startDate && format(new Date(this.range.end), 'yyyy-MM-dd') === this.pause.endDate) {
        this.closeModal();
        this.$fireToast({
          message: this.successMessage,
        });
      } else {
        try {
          const params = {
            unavailability_period_type: 'vacation',
            unavailability_period_range: {
              start_date: format(startOfDay(new Date(this.range.start)), 'yyyy-MM-dd'),
              end_date: format(startOfDay(new Date(this.range.end)), 'yyyy-MM-dd'),
            },
            lifecycle_status: 'active',
          };
          if (this.pause.serviceProviderOccupationId) {
            await this.updateServiceProviderOccupationUnavailability({
              unavailabilityId: this.pause.id,
              params,
            });
          } else {
            await this.updateServiceProviderUnavailability({
              unavailabilityId: this.pause.id,
              params,
            });
          }
          this.$events.$emit(
            this.$events.PRO_PAUSE_UPDATE,
            {
              occupation: this.pause.serviceProviderOccupationId ? this.displayText : 'All',
            });
          this.$fireToast({
            message: this.successMessage,
          });
        } catch (err) {
          console.error(err); // eslint-disable-line no-console
          this.$events.$emit(this.$events.PRO_PAUSE_UPDATE_ERROR, { errorMessage: err });
        }
        this.closeModal();
      }
    },
    moveToDate () {
      if (!isBefore(new Date(this.range.start), new Date())) {
        setTimeout(() => {
          const calendar = this.$refs.modalCalendar;
          calendar.move(new Date(this.range.start));
        }, 100);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .MODAL-WIDTH {
    @include responsive(width, 95vw, $unit * 78.25);
    @include responsive(height, 85vh);
  }
  .MODAL-CONTENT {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    @include responsive(width, 95vw, $unit * 37.5);


  }
  .MODAL-FOOTER {
    display: flex;
    background-color: white;
  }
  .ERROR-ICON {
      padding-top: 4px;
      flex: 0 0 auto;
      color: $color-red;
      background-color: white;
      font-size: 14px;
    }
  .LISTBOX {
    z-index: 99;
  }
  .INPUT {
  position: initial;
  text-align: center;
  width: 15.75 * $unit;
  height: 5 * $unit;
  min-width: calc(50% );
   &:active {
    outline-color: $color-depot;
  }
   &:focus-visible {
    outline-color: $color-depot;
  }
  }
  .LABEL {
    font-size: 14px;
  }
  .BUTTON {
    width: $unit * 31;
  }
  /deep/.custom-calendar {
  overflow:hidden;
  border-radius: 0;

  & .vc-container {
    --depot-100: #F96302;
    --depot-200: #f9630246;;
    --depot-300: #F96302;
    --depot-400: $color-gray80;
    --depot-500: #F96302;
    --depot-600: #F96302;
    --depot-700: #F96302;
    --depot-800: #F96302;
    --depot-900: $color-gray80;
    --rounded-full: 0;

  }
  & .vc-day-content{
    font-size: 16px;
  }
  & .vc-day-content:hover{
    background-color: rgba(0,0,0,.0);
  }
  & .vc-day-content:focus{
    background-color: rgba(0,0,0,.0);
  }
}
.ICON {
  position: relative;
  top: 3px;
  margin-right: $unit;
  color: #3E7697;

}
.TEXT {
  font-size: 14px;
}
</style>
